import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { alsokTheme } from '../styles/alsok-theme'
import { avatarService } from '../services/avatarService'
import { LipSyncManager, createSimpleLipSync } from '../utils/lipSync'

const PhotoRealisticAvatar = forwardRef(({ status, message, isListening, isSpeaking, avatarId = 'sato', audioElement, size = 'default' }, ref) => {
  const [currentAvatar, setCurrentAvatar] = useState(null)
  const [mouthAnimation, setMouthAnimation] = useState(0)
  const [expressionOverlay, setExpressionOverlay] = useState('neutral')
  const [isAvatarLoaded, setIsAvatarLoaded] = useState(false)
  const lipSyncRef = useRef(null)
  
  // アバターデータの読み込み
  useEffect(() => {
    const loadAvatar = async () => {
      try {
        if (!avatarService.isLoaded) {
          await avatarService.loadAvatarData()
        }
        const avatar = avatarService.getAvatarById(avatarId)
        setCurrentAvatar(avatar)
        setIsAvatarLoaded(true)
      } catch (error) {
        console.error('Failed to load avatar:', error)
      }
    }
    
    loadAvatar()
  }, [avatarId])
  
  // リップシンク初期化
  useEffect(() => {
    const initializeLipSync = async () => {
      if (typeof window !== 'undefined' && window.AudioContext) {
        lipSyncRef.current = new LipSyncManager()
        await lipSyncRef.current.initializeAudioContext()
      }
    }
    
    initializeLipSync()
    
    return () => {
      if (lipSyncRef.current) {
        lipSyncRef.current.destroy()
      }
    }
  }, [])
  
  
  // 音声要素が変更された時のリップシンク設定
  useEffect(() => {
    if (audioElement && isSpeaking && lipSyncRef.current) {
      lipSyncRef.current.attachToAudio(audioElement, (openness) => {
        setMouthAnimation(openness)
      })
    } else if (!isSpeaking) {
      setMouthAnimation(0)
    }
  }, [audioElement, isSpeaking])
  
  // 外部からアクセス可能なメソッドを公開
  useImperativeHandle(ref, () => ({
    setAudioElement: (audio) => {
      if (lipSyncRef.current && audio && isSpeaking) {
        lipSyncRef.current.attachToAudio(audio, setMouthAnimation)
      }
    },
    changeAvatar: (newAvatarId) => {
      const avatar = avatarService.getAvatarById(newAvatarId)
      if (avatar) {
        setCurrentAvatar(avatar)
      }
    }
  }))
  
  // 状態に応じた表情オーバーレイ
  useEffect(() => {
    switch (status) {
      case 'listening':
        setExpressionOverlay('attentive')
        break
      case 'speaking':
        setExpressionOverlay('friendly')
        break
      case 'processing':
        setExpressionOverlay('thinking')
        break
      default:
        setExpressionOverlay('neutral')
    }
  }, [status])
  
  // サイズに応じたスタイル設定
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return {
          containerGap: '10px',
          containerPadding: '10px',
          frameWidth: '200px',
          frameHeight: '250px',
          frameBorder: '3px',
          messageMaxWidth: '220px',
          messageText: '14px'
        }
      case 'fullscreen':
        return {
          containerGap: '30px',
          containerPadding: '30px',
          frameWidth: '800px',
          frameHeight: '1000px',
          frameBorder: '8px',
          messageMaxWidth: '600px',
          messageText: '24px'
        }
      default: // 'default'
        return {
          containerGap: '20px',
          containerPadding: '20px',
          frameWidth: '600px',
          frameHeight: '750px',
          frameBorder: '6px',
          messageMaxWidth: '350px',
          messageText: '16px'
        }
    }
  }
  
  const sizeStyles = getSizeStyles()
  
  return (
    <div className={`photo-avatar-container size-${size}`}>
      <div className={`avatar-frame ${status}`}>
        {/* ベース写真 */}
        <div className="base-photo">
          {isAvatarLoaded && currentAvatar ? (
            <img 
              src={avatarService.getAvatarImageUrl(avatarId)} 
              alt={`${currentAvatar.name} - ${currentAvatar.department}`}
              className="avatar-photo"
              onError={(e) => {
                // フォールバック順序：PNG → SVG → デフォルト画像
                if (e.target.src.includes('.png') && !e.target.src.includes('default')) {
                  e.target.src = `/avatars/photos/${avatarId}.svg`
                } else if (e.target.src.includes('.svg')) {
                  e.target.src = '/avatars/photos/default.png'
                } else {
                  e.target.src = '/avatars/photos/sato.svg' // 最終フォールバック
                }
              }}
              onLoad={() => {
                console.log(`Avatar image loaded: ${avatarService.getAvatarImageUrl(avatarId)}`)
              }}
            />
          ) : (
            <div className="loading-placeholder">
              <div className="loading-spinner"></div>
              <p>読み込み中...</p>
            </div>
          )}
        </div>
        
        {/* 表情オーバーレイ */}
        {isAvatarLoaded && currentAvatar && (
          <div className={`expression-overlay ${expressionOverlay}`}>
            {/* 目のオーバーレイ（アバターの顔の特徴に基づいて位置調整） */}
            
            {/* 口のオーバーレイ */}
            <div 
              className="mouth-overlay"
              style={{
                left: `${currentAvatar.faceFeatures.mouthPosition.x}%`,
                top: `${currentAvatar.faceFeatures.mouthPosition.y}%`
              }}
            >
              <div 
                className="mouth-animation"
                style={{
                  transform: `scaleY(${1 + mouthAnimation * 0.8}) scaleX(${1 + mouthAnimation * 0.3})`,
                  opacity: mouthAnimation > 0 ? 0.8 : 0.1
                }}
              ></div>
            </div>
          </div>
        )}
        
        {/* ステータスインジケーター */}
        <div className={`status-indicator ${status}`}>
          <div className="pulse-ring"></div>
        </div>
      </div>
      
      {/* アバター情報 */}
      {isAvatarLoaded && currentAvatar && size !== 'fullscreen' && (
        <div className="avatar-info">
          <div className="name-plate">
            <h3>{currentAvatar.name}</h3>
            <p>{currentAvatar.department}</p>
          </div>
        </div>
      )}
      
      {/* メッセージバブル */}
      {/* message-bubbleセクションを削除 */}
      
      <style jsx="true">{`
        .photo-avatar-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: ${sizeStyles.containerGap};
          padding: ${sizeStyles.containerPadding};
          animation: fadeInUp 0.8s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        @keyframes glow {
          0%, 100% { box-shadow: ${alsokTheme.shadows.security}; }
          50% { 
            box-shadow: ${alsokTheme.shadows.security}, 
                        0 0 20px rgba(0,102,204,0.3),
                        0 0 40px rgba(0,102,204,0.1);
          }
        }
        
        .avatar-frame {
          position: relative;
          width: ${sizeStyles.frameWidth};
          height: ${sizeStyles.frameHeight};
          border-radius: ${alsokTheme.borderRadius.xl};
          overflow: hidden;
          box-shadow: ${alsokTheme.shadows.security};
          border: ${sizeStyles.frameBorder} solid ${alsokTheme.primary.light};
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          transition: all 0.4s ease;
        }

        .avatar-frame:hover {
        }

        .avatar-frame.speaking {
          border-color: ${alsokTheme.accent.green};
        }

        .avatar-frame.listening {
          border-color: ${alsokTheme.primary.main};
        }

        .avatar-frame.processing {
          border-color: ${alsokTheme.status.warning};
        }
        
        .base-photo {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .avatar-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
        }
        
        .expression-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          transition: all 0.3s ease;
        }
        
        
        .mouth-overlay {
          position: absolute;
          transform: translate(-50%, -50%);
          width: 45px;
          height: 25px;
        }
        
        .mouth-animation {
          width: 100%;
          height: 100%;
          background: radial-gradient(ellipse at center, rgba(255,107,157,0.6) 0%, rgba(255,255,255,0.4) 30%, transparent 70%);
          border-radius: 50%;
          transform-origin: center;
          transition: all 0.1s ease-out;
        }
        
        .status-indicator {
          position: absolute;
          bottom: 10px;
          right: 10px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${alsokTheme.status.success};
        }
        
        .status-indicator.listening {
          background: ${alsokTheme.status.info};
          animation: pulse 1s ease-in-out infinite;
        }
        
        .status-indicator.speaking {
          background: ${alsokTheme.accent.green};
          animation: pulse 0.5s ease-in-out infinite;
        }
        
        .status-indicator.processing {
          background: ${alsokTheme.status.warning};
          animation: spin 1s linear infinite;
        }
        
        .pulse-ring {
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          border: 2px solid currentColor;
          border-radius: 50%;
          opacity: 0;
        }
        
        .status-indicator.listening .pulse-ring {
          animation: ring 1.5s ease-out infinite;
        }
        
        .message-container {
          /* メッセージバブル関連のCSSを削除 */
        }
        
        .message-bubble {
          /* メッセージバブル関連のCSSを削除 */
        }
        .message-bubble:hover {
          /* メッセージバブル関連のCSSを削除 */
        }
        .message-bubble::before {
          /* メッセージバブル関連のCSSを削除 */
        }
        .message-bubble::after {
          /* メッセージバブル関連のCSSを削除 */
        }
        @keyframes messageSlideIn {
          /* メッセージバブル関連のCSSを削除 */
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
        
        @keyframes ring {
          0% { opacity: 0; transform: scale(1); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: scale(1.5); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .loading-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: ${alsokTheme.neutral.mediumGray};
        }
        
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid ${alsokTheme.neutral.lightGray};
          border-top: 4px solid ${alsokTheme.primary.main};
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: ${alsokTheme.spacing.md};
        }
        
        .avatar-info {
          margin-bottom: ${alsokTheme.spacing.md};
        }
        
        .name-plate {
          background: linear-gradient(135deg, ${alsokTheme.primary.main} 0%, ${alsokTheme.primary.light} 100%);
          color: ${alsokTheme.neutral.white};
          padding: ${alsokTheme.spacing.sm} ${alsokTheme.spacing.lg};
          border-radius: ${alsokTheme.borderRadius.lg};
          text-align: center;
          box-shadow: ${alsokTheme.shadows.md};
        }
        
        .name-plate h3 {
          margin: 0;
          font-size: ${alsokTheme.typography.fontSize.lg};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
        }
        
        .name-plate p {
          margin: ${alsokTheme.spacing.xs} 0 0 0;
          font-size: ${alsokTheme.typography.fontSize.sm};
          opacity: 0.9;
        }
      `}</style>
    </div>
  )
})

export default PhotoRealisticAvatar